import { z } from 'zod';
import type { DocumentEvidenceFact } from './siteEvidence';

export const SITE_RESOURCE_KEYS = ['netUsableAcres', 'siteWaterAllocationMgd', 'minimumWaterPressurePsi', 'waterServiceDate',
  'projectAverageWaterDemandMgd', 'projectPeakWaterDemandMgd', 'projectWaterMarginMgd', 'siteWastewaterAllocationMgd', 'projectPeakWastewaterDischargeMgd', 'projectWastewaterMarginMgd'] as const;
export type SiteResourceKey = typeof SITE_RESOURCE_KEYS[number];
const qualification = { fictional: z.boolean(), assumed: z.boolean() };
const quantity = z.number().finite().nonnegative();
const calendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(value => {
  const [year, month, day] = value.split('-').map(Number);
  if (year === undefined || month === undefined || day === undefined || month < 1 || month > 12 || day < 1) return false;
  const max = [31, year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1];
  return max !== undefined && day <= max;
}, 'Source service date must be a real calendar date.');

/** Render-only quantities: no owner, document handles, source quotes or storage paths. */
export const siteResourceViewSchema = z.object({
  schemaVersion: z.literal(1),
  facts: z.array(z.discriminatedUnion('key', [
    z.object({ key: z.literal('netUsableAcres'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('siteWaterAllocationMgd'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('minimumWaterPressurePsi'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('waterServiceDate'), value: calendarDate, ...qualification }).strict(),
    z.object({ key: z.literal('projectAverageWaterDemandMgd'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('projectPeakWaterDemandMgd'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('projectWaterMarginMgd'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('siteWastewaterAllocationMgd'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('projectPeakWastewaterDischargeMgd'), value: quantity, ...qualification }).strict(),
    z.object({ key: z.literal('projectWastewaterMarginMgd'), value: quantity, ...qualification }).strict(),
  ])),
  conflicts: z.array(z.enum(SITE_RESOURCE_KEYS)),
}).strict().superRefine((view, ctx) => {
  const keys = view.facts.map(fact => fact.key);
  if (new Set(keys).size !== keys.length || new Set(view.conflicts).size !== view.conflicts.length
    || keys.some(key => view.conflicts.includes(key))) ctx.addIssue({ code: 'custom', message: 'Resource quantities must be unique and unresolved conflicts cannot supply a value.' });
});
export type SiteResourceView = z.infer<typeof siteResourceViewSchema>;

/** Private immutable source witness. It remains a statement, never an adopted
 * design input, professional surveyed area or utility commitment. */
export interface SiteResourceSourceRow {
  key: SiteResourceKey;
  value: number | string;
  fictional: boolean;
  assumed: boolean;
  bodySha256: string;
  originalFileSha256: string;
  scenarioId?: string;
  /** Complete local source context; no reference to a model-generated summary. */
  context: string;
  fact: DocumentEvidenceFact;
}
export interface SiteResourceDocumentEvidence {
  schemaVersion: 1;
  rows: SiteResourceSourceRow[];
  sourceHash: string;
}
